import { useMemo, useCallback, useState } from 'react';
import { startOfDay, addMonths, addDays, format } from 'date-fns';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { VacationHelp, VACATION_OVERVIEW_DASHBOARD_STEPS, VACATION_OVERVIEW_TABLE_STEPS, OVERVIEW_RULES } from '../../components/VacationHelp';
import { useVacationOverviewData } from '../../hooks/data/useVacationOverviewQuery';
import { useVacationOverviewFilters } from '../../hooks/useVacationOverviewFilters';
import { useOverviewScreenFilters } from '../../hooks/useOverviewScreenFilters';
import { useOverviewState } from '../../hooks/useOverviewState';
import { OverviewHeaderActions } from './components/OverviewHeaderActions';
import {
	DashboardSkeleton,
	OverviewCarousel,
	OverviewKpiRow,
	DashboardScreen,
	TableScreen,
} from './components';
import type { OverviewRange } from './components';
import { buildTodaySnapshot } from '../../utils/panorama/panoramaToday';
import { buildCapacitySnapshot } from '../../utils/panorama/panoramaCapacity';
import { buildClientDeparturesSnapshot } from '../../utils/panorama/panoramaClientDepartures';
import { buildDeadlinesByMonth } from '../../utils/panorama/panoramaDeadlinesByMonth';
import { buildFilterOptions } from '../../utils/panorama/panoramaFilters';
import { buildPeopleRows, filterPeopleRows, countByStatus } from '../../utils/panorama/panoramaPeopleRows';
import { summarizePending } from '../../utils/panorama/panoramaRowWindow';
import { earliestVacationDate, validateRange } from '../../utils/panorama/panoramaPeriod';
import { toIsoDay } from '../../utils/overviewDates';
import type { Status } from '../../utils/panorama/panoramaPeopleRows';
import type { FilterField } from './components/OverviewFilters';
import {
	CAPACITY_LIMIT_PERCENT,
	ERROR_MESSAGE,
	PAGE_TITLE,
} from './vacationOverviewPage.definitions';

export function VacationOverviewPage() {
	const overview = useVacationOverviewData();
	const dashboardFilters = useVacationOverviewFilters(overview.docs);
	const filtered = dashboardFilters.filteredDocuments;
	const { screen: activeScreen, setScreen, tableFilters, setTableFilters, showTableWithStatuses } =
		useOverviewScreenFilters();

	const referenceDate = useMemo(() => startOfDay(new Date()), []);
	const { get, setMany } = useOverviewState();
	const periodFrom = useMemo(() => get('d_de'), [get]);
	const periodTo = useMemo(() => get('d_ate'), [get]);
	const [deadlineMonthsAhead, setDeadlineMonthsAhead] = useState(12);

	const earliestDate = useMemo(() => earliestVacationDate(overview.docs), [overview.docs]);
	const tableRange = useMemo(() => {
		if (tableFilters.periodFrom && tableFilters.periodTo && validateRange({ from: tableFilters.periodFrom, to: tableFilters.periodTo }, earliestDate ?? undefined)) {
			return { from: tableFilters.periodFrom, to: tableFilters.periodTo };
		}
		return undefined;
	}, [tableFilters.periodFrom, tableFilters.periodTo, earliestDate]);

	const effectivePeriodFrom = useMemo(() => {
		if (periodFrom) return periodFrom;
		return format(referenceDate, 'yyyy-MM-dd');
	}, [periodFrom, referenceDate]);

	const effectivePeriodTo = useMemo(() => {
		if (periodTo) return periodTo;
		return format(addDays(referenceDate, 90), 'yyyy-MM-dd');
	}, [periodTo, referenceDate]);

	const dashboardRange = useMemo(() => {
		const range = { from: effectivePeriodFrom, to: effectivePeriodTo };
		if (validateRange(range, earliestDate ?? undefined)) {
			return range;
		}
		return { from: format(referenceDate, 'yyyy-MM-dd'), to: format(addDays(referenceDate, 90), 'yyyy-MM-dd') };
	}, [effectivePeriodFrom, effectivePeriodTo, referenceDate, earliestDate]);

	const periodSubtitle = effectivePeriodFrom && effectivePeriodTo
		? `O período vale para "Pessoas em férias por semana" e "Saídas por cliente".`
		: undefined;

	const todayRange = useMemo(() => ({ from: format(referenceDate, 'yyyy-MM-dd'), to: format(referenceDate, 'yyyy-MM-dd') }), [referenceDate]);
	const today = useMemo(() => buildTodaySnapshot(filtered, referenceDate), [filtered, referenceDate]);
	const capacity = useMemo(
		() => buildCapacitySnapshot(filtered, referenceDate, CAPACITY_LIMIT_PERCENT, dashboardRange),
		[filtered, referenceDate, dashboardRange]
	);
	const clientDepartures = useMemo(() => buildClientDeparturesSnapshot(filtered, referenceDate, dashboardRange), [filtered, referenceDate, dashboardRange]);
	const deadlinesByMonth = useMemo(() => buildDeadlinesByMonth(filtered, referenceDate, deadlineMonthsAhead), [filtered, referenceDate, deadlineMonthsAhead]);

	const personRows = useMemo(
		() => buildPeopleRows(filtered, referenceDate, tableRange, { includeApprovalDeadline: true }),
		[filtered, referenceDate, tableRange]
	);
	const filteredPersonRows = useMemo(() => filterPeopleRows(personRows, tableFilters), [personRows, tableFilters]);
	const dashboardPersonRows = useMemo(() => buildPeopleRows(filtered, referenceDate), [filtered, referenceDate]);
	const statusCounts = useMemo(() => countByStatus(dashboardPersonRows), [dashboardPersonRows]);
	const pendingSummary = useMemo(() => summarizePending(dashboardPersonRows, referenceDate), [dashboardPersonRows, referenceDate]);
	const tableFilterOptions = useMemo(() => buildFilterOptions(filtered), [filtered]);
	const expiredCount = useMemo(
		() => dashboardPersonRows.filter(r => r.status === 'expired').length,
		[dashboardPersonRows]
	);
	const expiringCount = useMemo(
		() => dashboardPersonRows.filter(r => r.status === 'expiring-soon').length,
		[dashboardPersonRows]
	);
	const riskCount = expiredCount + expiringCount;

	const nextMonthDate = addMonths(referenceDate, 1);

	const nextMonthFrom = useMemo(
		() => toIsoDay(new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), 1)),
		[nextMonthDate]
	);
	const nextMonthTo = useMemo(
		() => toIsoDay(addDays(new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth() + 1, 0), 0)),
		[nextMonthDate]
	);

	const nextMonthCount = useMemo(
		() => buildPeopleRows(filtered, referenceDate, { from: nextMonthFrom, to: nextMonthTo })
			.filter(r => (r.status === 'scheduled' || r.status === 'on-vacation')).length,
		[filtered, referenceDate, nextMonthFrom, nextMonthTo]
	);

	const nextMonthRange = useMemo<OverviewRange>(() => ({ from: nextMonthFrom, to: nextMonthTo }), [nextMonthFrom, nextMonthTo]);

	const handlePeriodChange = useCallback((from: string | null, to: string | null) => {
		setMany({ d_de: from || null, d_ate: to || null });
	}, [setMany]);

	const dashboardFilterFields = useMemo<FilterField[]>(() => [
		{
			type: 'period',
			key: 'period',
			from: periodFrom,
			to: periodTo,
			onChange: handlePeriodChange,
			today: referenceDate,
			minDate: earliestDate ?? undefined,
		},
		{
			type: 'multi',
			key: 'collaborators',
			label: 'Colaborador',
			options: dashboardFilters.filterOptions.collaborators,
			values: dashboardFilters.filters.collaborators,
			onChange: values => dashboardFilters.setFilter('collaborators', values),
		},
		{
			type: 'multi',
			key: 'clients',
			label: 'Cliente',
			options: dashboardFilters.filterOptions.clients,
			values: dashboardFilters.filters.clients,
			onChange: values => dashboardFilters.setFilter('clients', values),
		},
	], [dashboardFilters, periodFrom, periodTo, handlePeriodChange, referenceDate, earliestDate]);

	const handleKpiShowTable = useCallback((statuses: Status[], range?: OverviewRange) => {
		showTableWithStatuses(statuses, range ? { periodFrom: range.from, periodTo: range.to } : undefined);
	}, [showTableWithStatuses]);

	const handleClearStatuses = useCallback(() => {
		setTableFilters({ ...tableFilters, statuses: [] });
	}, [setTableFilters, tableFilters]);

	const handleStatusClick = useCallback((status: Status) => {
		showTableWithStatuses([status]);
	}, [showTableWithStatuses]);

	const handleWeekClick = useCallback((weekStart: string, statuses: Status | Status[]) => {
		const statusList = Array.isArray(statuses) ? statuses : [statuses];
		const weekStartDate = new Date(weekStart + 'T00:00:00');
		const endDate = toIsoDay(addDays(weekStartDate, 6));
		showTableWithStatuses(statusList, { periodFrom: weekStart, periodTo: endDate });
	}, [showTableWithStatuses]);

	const handleMonthClick = useCallback((year: number, monthNum: number, isPast: boolean) => {
		const from = toIsoDay(new Date(year, monthNum - 1, 1));
		const to = toIsoDay(addDays(new Date(year, monthNum, 0), 0));
		showTableWithStatuses(isPast ? ['expired'] : [], { periodFrom: from, periodTo: to, prazoMes: `${year}-${String(monthNum).padStart(2, '0')}` });
	}, [showTableWithStatuses]);

	const handleStageClick = useCallback((stage: 'manager' | 'client' | 'dp' | 'correction') => {
		showTableWithStatuses(['pending', 'approval-urgent'], { stages: [stage] });
	}, [showTableWithStatuses]);

	const handleClientClick = useCallback((clientName: string, statuses: Status[]) => {
		showTableWithStatuses(statuses, { clients: [clientName], periodFrom: dashboardRange.from, periodTo: dashboardRange.to });
	}, [showTableWithStatuses, dashboardRange]);

	const slides = [
		{
			id: 'dashboard',
			label: 'Dashboard',
			content: (
				<DashboardScreen
					today={today}
					capacity={capacity}
					statusCounts={statusCounts}
					clientDepartures={clientDepartures}
					deadlinesByMonth={deadlinesByMonth}
					referenceDate={referenceDate}
					capacityLimitPercent={CAPACITY_LIMIT_PERCENT}
					filterFields={dashboardFilterFields}
					onClearFilters={() => {
						dashboardFilters.clearFilters();
						setMany({ d_de: null, d_ate: null });
					}}
					onStatusClick={handleStatusClick}
					onWeekClick={handleWeekClick}
					onMonthClick={handleMonthClick}
					onClientClick={handleClientClick}
					onStageClick={handleStageClick}
					pendingCounts={pendingSummary.byStage}
					pendingTotal={pendingSummary.total}
					deadlineMonthsAhead={deadlineMonthsAhead}
					onDeadlineMonthsAheadChange={setDeadlineMonthsAhead}
					periodSubtitle={periodSubtitle}
				/>
			),
		},
		{
			id: 'table',
			label: 'Tabela',
			content: (
				<TableScreen
					rows={filteredPersonRows}
					filters={tableFilters}
					onFiltersChange={setTableFilters}
					allRows={personRows}
					clientOptions={tableFilterOptions.clients}
					allDocsRows={personRows}
					referenceDate={referenceDate}
					minDate={earliestDate ?? undefined}
				/>
			),
		},
	];

	if (overview.error) {
		return (
			<div className="space-y-4">
				<SectionHeader title={PAGE_TITLE} />
				<div className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
					<p className="text-sm text-red-700 font-montserrat">{ERROR_MESSAGE}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<SectionHeader
				title={PAGE_TITLE}
				actions={
					<div className="flex items-center gap-4">
						<OverviewHeaderActions
							loadedAt={overview.loadedAt}
							isFetching={overview.isFetching}
							onRefresh={overview.refresh}
						/>
						<VacationHelp
							tourId={activeScreen === 'dashboard' ? 'overview-dashboard' : 'overview-table'}
							steps={activeScreen === 'dashboard' ? VACATION_OVERVIEW_DASHBOARD_STEPS : VACATION_OVERVIEW_TABLE_STEPS}
							rules={OVERVIEW_RULES}
							autoStartTour={false}
						/>
					</div>
				}
			/>

			{overview.isLoading ? (
				<DashboardSkeleton />
			) : (
				<div className="space-y-4">
					<OverviewKpiRow
						activeScreen={activeScreen}
						tableFilters={tableFilters}
						today={today}
						todayRange={todayRange}
						nextMonthDate={nextMonthDate}
						nextMonthCount={nextMonthCount}
						nextMonthRange={nextMonthRange}
						statusCounts={statusCounts}
						pendingSummary={pendingSummary}
						expiredCount={expiredCount}
						expiringCount={expiringCount}
						riskCount={riskCount}
						onShowTable={handleKpiShowTable}
						onClearStatuses={handleClearStatuses}
					/>

					<OverviewCarousel
						slides={slides}
						activeSlideId={activeScreen}
						onSlideChange={id => setScreen(id === 'table' ? 'table' : 'dashboard')}
					/>
				</div>
			)}
		</div>
	);
}
